import type { UniversalChart, ChartColor, ChartType as SharedChartType, ChartConfiguration } from '@shared/schemas/chart';
import type { ChartData } from '../store/chartAtoms';
import type { StandardizedDataPoint, UniversalChartConfig } from '../components/charts/UniversalChartRenderer';
import { getChartColors, generateMonochromaticPalette } from './chartUtils';
import { getColorPalette, type ColorPalette, COLOR_PALETTES } from './colorPalettes';
import { getColorForCurrentTheme } from './themeAwareColors';
import { getChartColorMode } from './chartColorUtils';

/**
 * Normalizes any chart data to the standardized format
 * Works with both saved and unsaved UniversalChart objects
 */
export function normalizeChartData(
  chart: ChartData,
  options: {
    height?: number;
    enableAdvancedFeatures?: boolean;
    className?: string;
    customPalettes?: ColorPalette[];
    isDarkMode?: boolean;
    globalDefaultPaletteId?: string;
  } = {}
): UniversalChartConfig {
  console.log('🔄 [normalizeChartData] Normalizing chart:', chart.title);
  console.log('🔄 [normalizeChartData] Chart saved status:', chart.savedToDatabase);
  console.log('🔄 [normalizeChartData] Chart configuration:', JSON.parse(JSON.stringify(chart.configuration)));
  
  if (!chart || !chart.data || chart.data.length === 0) {
    console.log('🔄 [normalizeChartData] No data found, returning empty config');
    const fallbackColors = determineChartColors(chart?.chartType || 'bar', chart?.configuration, undefined, 0, {
      isDarkMode: options.isDarkMode,
      customPalettes: options.customPalettes,
    }, chart);
    return {
      data: [],
      chartType: chart?.chartType || 'bar',
      studies: chart?.studies || [],
      axisOptions: chart?.axisOptions,
      displayOptions: chart?.displayOptions,
      height: options.height,
      enableAdvancedFeatures: options.enableAdvancedFeatures,
      colors: fallbackColors,
      isTimeBasedData: false,
      metadata: chart?.metadata,
    };
  }

  // Detect if this is time-based data
  let isTimeBasedData = false;
  if (chart.xAxis && chart.data.length > 0) {
    const sampleXValue = chart.data[0][chart.xAxis];
    if (typeof sampleXValue === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(sampleXValue) || 
          /^\d{1,2}\/\d{1,2}\/\d{2,4}/.test(sampleXValue) ||
          /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b[\s-]*\d{2,4}/i.test(sampleXValue) ||
          !isNaN(new Date(sampleXValue).getTime())
      ) {
        isTimeBasedData = !isNaN(new Date(sampleXValue).getTime());
      }
    }
  }
  console.log('🔄 [normalizeChartData] Detected isTimeBasedData:', isTimeBasedData);

  const numDataPoints = chart.data.length;
  console.log('🔄 [normalizeChartData] Number of data points:', numDataPoints);

  // Standardize the data points
  const standardizedData: StandardizedDataPoint[] = chart.data.map((row, index) => {
    if (chart.chartType === 'pie' || chart.chartType === 'doughnut') {
      const rawNameValue = chart.xAxis && row[chart.xAxis] !== undefined ? row[chart.xAxis] : undefined;
      const nameValue = rawNameValue !== undefined && rawNameValue !== null ? String(rawNameValue) : `Category ${index + 1}`;
      const yValue = chart.yAxis && row[chart.yAxis] !== undefined ? Number(row[chart.yAxis]) : 0;
      
      return {
        ...row,
        name: nameValue || `Category ${index + 1}`,
        value: isNaN(yValue) ? 0 : yValue,
        index: index,
      };
    }

    const rawNameValue = chart.xAxis && row[chart.xAxis] !== undefined ? row[chart.xAxis] : undefined;
    const nameValue = rawNameValue !== undefined && rawNameValue !== null ? String(rawNameValue) : `Point ${index + 1}`;
    const yValue = chart.yAxis && row[chart.yAxis] !== undefined ? Number(row[chart.yAxis]) : (row.value !== undefined ? Number(row.value) : 0);
    
    // Debug logging for saved charts to see what's happening with the names
    if (chart.savedToDatabase) {
      console.log(`[normalizeChartData] Row ${index}:`, {
        xAxis: chart.xAxis,
        rawNameValue,
        nameValue,
        rowKeys: Object.keys(row),
        rowData: row
      });
    }
    
    return {
      ...row,
      name: nameValue || `Point ${index + 1}`, // Ensure name is never undefined
      value: isNaN(yValue) ? 0 : yValue, // Ensure value is never NaN
      index: index,
    };
  });

  console.log('🔄 [normalizeChartData] Standardized data points:', standardizedData.length);

  const determinedColors = determineChartColors(
    chart.chartType,
    chart.configuration,
    chart.colors,
    numDataPoints,
    options,
    chart // Pass the chart object for series detection
  );

  console.log('🔄 [normalizeChartData] Determined colors:', determinedColors);

  return {
    data: standardizedData,
    chartType: chart.chartType,
    studies: chart.studies || [],
    axisOptions: chart.axisOptions,
    displayOptions: chart.displayOptions,
    height: options.height,
    colors: determinedColors,
    datasetLabel: chart.title || 'Data',
    isTimeBasedData,
    enableAdvancedFeatures: options.enableAdvancedFeatures,
    metadata: chart.metadata,
  };
}

/**
 * Utility function to create a quick config for simple chart rendering
 */
export function createQuickChartConfig(
  data: any[],
  chartType: string,
  options: {
    xKey?: string;
    yKey?: string;
    height?: number;
    showGridLines?: boolean;
    studies?: any[];
  } = {}
): UniversalChartConfig {
  const { xKey = 'name', yKey = 'value', height, showGridLines = true, studies = [] } = options;

  const standardizedData: StandardizedDataPoint[] = data.map((item, index) => ({
    name: item[xKey] || `Point ${index + 1}`,
    value: Number(item[yKey]) || 0,
    index: index,
    ...item,
  }));

  return {
    data: standardizedData,
    chartType: chartType as any,
    studies,
    axisOptions: null,
    displayOptions: { showGridLines },
    height,
    enableAdvancedFeatures: false,
    metadata: undefined,
  };
}

// Common logic for determining colors
function determineChartColors(
  chartType: SharedChartType,
  configuration: Partial<ChartConfiguration> | undefined,
  existingColors: string[] | undefined, 
  numDataPoints: number,
  options: { 
    customPalettes?: ColorPalette[],
    isDarkMode?: boolean,
  },
  chart?: ChartData
): string[] {
  let colors: string[] = [];
  const colorMode = getChartColorMode(chartType);
  const chartConfig = configuration || {};
  const isDarkMode = options.isDarkMode === undefined ? false : options.isDarkMode;

  let chosenSingleColor: string | undefined = undefined;

  if (chartConfig.singleColor) {
    chosenSingleColor = getColorForCurrentTheme(chartConfig.singleColor, isDarkMode);
  } else if (chartConfig.colorPalette) {
    const paletteOutput = getColorPalette(chartConfig.colorPalette, options.customPalettes);
    
    if (paletteOutput) {
      if (Array.isArray(paletteOutput) && paletteOutput.length > 0) {
        chosenSingleColor = getColorForCurrentTheme(paletteOutput[0], isDarkMode);
      } else if (typeof paletteOutput === 'object' && !Array.isArray(paletteOutput) && (paletteOutput as any).colors && Array.isArray((paletteOutput as any).colors) && (paletteOutput as any).colors.length > 0) {
        const paletteDetails = paletteOutput as ColorPalette;
        let paletteDefaultColorValue: ChartColor | undefined = undefined;
        const paletteColorsArray = paletteDetails.colors;

        if (paletteDetails.defaultColorIndex !== undefined && 
            paletteDetails.defaultColorIndex >= 0 && 
            paletteDetails.defaultColorIndex < paletteColorsArray.length) {
          paletteDefaultColorValue = paletteColorsArray[paletteDetails.defaultColorIndex];
        } else {
          paletteDefaultColorValue = paletteColorsArray[0]; 
        }

        if (paletteDefaultColorValue) {
          chosenSingleColor = getColorForCurrentTheme(paletteDefaultColorValue, isDarkMode);
        }
      }
    }
  }

  if (!chosenSingleColor) {
    chosenSingleColor = getColorForCurrentTheme('#FF4500', isDarkMode);
  }

  // Detect multi-series data by checking if first data point has multiple numeric values
  let numSeries = 1;
  if (chart && chart.data && chart.data.length > 0) {
    const firstDataPoint = chart.data[0];
    const numericKeys = Object.keys(firstDataPoint).filter(key => 
      key !== chart.xAxis && 
      typeof firstDataPoint[key] === 'number'
    );
    numSeries = Math.max(numericKeys.length, 1);
  }

  // Check if we need multiple colors - either chart type requires palette OR we have multi-series data OR a palette is explicitly specified
  const needsMultipleColors = colorMode.requiresPalette || numDataPoints > 1 || numSeries > 1 || chartConfig.colorPalette;
  
  console.log('🎨 [determineChartColors] Chart Type:', chartType, 'colorPalette:', chartConfig.colorPalette, 'numDataPoints:', numDataPoints, 'numSeries:', numSeries, 'requiresPalette:', colorMode.requiresPalette, 'needsMultipleColors:', needsMultipleColors)

  if (needsMultipleColors) {
    // If we have a colorPalette specified, use the full palette colors instead of monochromatic
    if (chartConfig.colorPalette) {
      const paletteColors = getColorPalette(chartConfig.colorPalette, options.customPalettes);
      
      if (Array.isArray(paletteColors) && paletteColors.length > 0) {
        // For multi-series, use number of series; for single-series, use number of data points
        const shadesCount = Math.min(numSeries > 1 ? numSeries : (numDataPoints > 0 ? numDataPoints : 2), 12);
        colors = [];
        for (let i = 0; i < shadesCount; i++) {
          const paletteColor = paletteColors[i % paletteColors.length];
          const processedColor = getColorForCurrentTheme(paletteColor, isDarkMode);
          colors.push(processedColor);
        }
        console.log('🎨 [determineChartColors] Generated', colors.length, 'colors from palette (series:', numSeries, 'points:', numDataPoints, ')')
      } else {
        // Fallback to monochromatic if palette retrieval failed
        const shadesCount = Math.min(numSeries > 1 ? numSeries : (numDataPoints > 0 ? numDataPoints : 2), 12);
        colors = generateMonochromaticPalette(chosenSingleColor, shadesCount);
      }
    } else {
      // No palette specified, use monochromatic
      const shadesCount = Math.min(numSeries > 1 ? numSeries : (numDataPoints > 0 ? numDataPoints : 2), 12);
      colors = generateMonochromaticPalette(chosenSingleColor, shadesCount);
    }
  } else {
    colors = [chosenSingleColor];
  }

  console.log('🎨 [determineChartColors] Final colors:', colors)
  return colors;
} 